import * as React from "react";
import { Grid, GridColumn, Input } from "semantic-ui-react";
import "./project-edit.css";
import MyDatePicker from "../../../components/date-picker/date-picker";
import LabeledInput from "../../../components/labeled-input/labeled-input";
import LabeledTextArea from "../../../components/labeled-textarea/labeled-textarea";
import { KeyValueStore } from "../../../stores/key-value/key-value-store";
import { LandUsePlanChangeProject } from "../../../stores/project/project";
import LabeledValue from "../../../components/labeled-value/labeled-value";
import { Municipality } from "../../../stores/municipality/municipality-store";
import { getDesc, getLabel } from "../../../reflection/reflection";
import { Constants } from "../../../globals/constants";
import { useState } from "react";

export default function LandUsePlanChangeEdit({
                                                  project,
                                                  municipalities,
                                                  keyValueStore
                                              }: {
    project: LandUsePlanChangeProject;
    municipalities: Municipality[];
    keyValueStore: KeyValueStore
}): JSX.Element {

    function handleEnactmentDateChanged(enactmentDate: Date | null) {
        if (enactmentDate !== null) {
            const projectTypes = project.compensations.map(c => c.creationProject.typeKey);
            const newCompensationDeadline = new Date(enactmentDate);

            if (projectTypes.includes(Constants.projectTypeSoilImprovement)) {
                if (projectTypes.includes(Constants.projectTypeDezoning)) {
                    // Soil improvement and dezoning
                    newCompensationDeadline.setFullYear(newCompensationDeadline.getFullYear() + 5);
                    setCompensationDeadline(newCompensationDeadline);
                } else {
                    // Soil improvement only
                    newCompensationDeadline.setFullYear(newCompensationDeadline.getFullYear() + 5);
                    setCompensationDeadline(newCompensationDeadline);
                }
            } else if (projectTypes.includes(Constants.projectTypeDezoning)) {
                // Dezoning only
                setCompensationDeadline(newCompensationDeadline);
            }
        }
    }

    const [compensationDeadline, setCompensationDeadline] = useState<Date | null>(project.compensationDeadline);

    return (
        <div>
            <Grid columns={2} relaxed={true} stackable={true}>
                <GridColumn largeScreen={16} widescreen={8}>
                    <LabeledValue
                        label={getLabel(project, "fullType")}
                        value={project.type}
                        required={true}/>
                    <LabeledInput
                        label="Name"
                        inputElement={
                            <Input
                                type="text"
                                name="name"
                                defaultValue={project.name}
                            />
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "municipalityName")}
                        desc={getDesc(project, "municipalityName")}
                        inputElement={
                            <select name="municipality" className="ui selection dropdown">
                                <option disabled selected={project.municipality === undefined}>
                                    -- Auswählen --
                                </option>
                                {municipalities.map((m) => (
                                    <option
                                        key={m.bfsNr}
                                        value={m.bfsNr}
                                        selected={project.municipality?.bfsNr === m.bfsNr}
                                    >
                                        {m.name}
                                    </option>
                                ))}
                            </select>
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "place")}
                        desc={getDesc(project, "place")}
                        inputElement={<Input type="text" name="place" defaultValue={project.place}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "revision")}
                        desc={getDesc(project, "revision")}
                        inputElement={
                            <select name="revision" className="ui selection dropdown">
                                <option disabled selected={project.projectDto.revision === null}>
                                    -- Auswählen --
                                </option>
                                {keyValueStore.revisionTypes.map((r) => (
                                    <option
                                        key={r.key}
                                        value={r.key}
                                        selected={project.projectDto.revision === r.key}
                                    >
                                        {r.value}
                                    </option>
                                ))}
                            </select>
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "status")}
                        desc={getDesc(project, "status")}
                        inputElement={
                            <select name="status" className="ui selection dropdown">
                                {keyValueStore.projectStatuses.map((s) => (
                                    <option
                                        key={s.key}
                                        value={s.key}
                                        selected={project.projectDto.status === s.key}
                                    >
                                        {s.value}
                                    </option>
                                ))}
                            </select>
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "governmentCouncilDecisionNr")}
                        desc={getDesc(project, "governmentCouncilDecisionNr")}
                        inputElement={<Input type="text" name="governmentCouncilDecisionNr"
                                             defaultValue={project.governmentCouncilDecisionNr}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "ebageNr")}
                        desc={getDesc(project, "ebageNr")}
                        inputElement={<Input type="text" name="ebageNr" defaultValue={project.ebageNr}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "enactmentDateDisplayText")}
                        desc={getDesc(project, "enactmentDateDisplayText")}
                        inputElement={<MyDatePicker name="enactmentDate" value={project.enactmentDate}
                                                    onChange={(event, data) => handleEnactmentDateChanged(data.value as (Date | null))}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "targetArea")}
                        desc={getDesc(project, "targetArea")}
                        inputElement={<Input type="number" name="targetArea" defaultValue={project.targetArea}/>}
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "compensationDeadlineDisplayText")}
                        desc={getDesc(project, "compensationDeadlineDisplayText")}
                        inputElement={<MyDatePicker name="compensationDeadline" value={compensationDeadline}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsRawiPre")}
                        desc={getDesc(project, "cmiDocumentsRawiPre")}
                        inputElement={<Input type="text" name="cmiNrRawiPre"
                                             defaultValue={project.cmiDocumentsRawiPre.join(", ")}/>}
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsUwePre")}
                        desc={getDesc(project, "cmiDocumentsUwePre")}
                        inputElement={<Input type="text" name="cmiNrUwePre"
                                             defaultValue={project.cmiDocumentsUwePre.join(", ")}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsRawiApproved")}
                        desc={getDesc(project, "cmiDocumentsRawiApproved")}
                        inputElement={<Input type="text" name="cmiNrRawiApproved"
                                             defaultValue={project.cmiDocumentsRawiApproved.join(", ")}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsUweApproved")}
                        desc={getDesc(project, "cmiDocumentsUweApproved")}
                        inputElement={<Input type="text" name="cmiNrUweApproved"
                                             defaultValue={project.cmiDocumentsUweApproved.join(", ")}/>}
                    />
                </GridColumn>
                <GridColumn>
                    <LabeledInput
                        label={getLabel(project, "geometrySource")}
                        desc={getDesc(project, "geometrySource")}
                        inputElement={<Input type="text" maxLength={50} name="geometrySource"
                                             defaultValue={project.geometrySource}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "geometrySourceDateDisplayText")}
                        desc={getDesc(project, "geometrySourceDateDisplayText")}
                        inputElement={<MyDatePicker name="geometrySourceDate" value={project.geometrySourceDate}/>}
                    />
                    <LabeledTextArea label={getLabel(project, "remarks")}
                                     name="remarks"
                                     defaultValue={project.remarks}/>
                </GridColumn>
            </Grid>
        </div>
    );
}
